<template>
  <div class="web-gl">
    <canvas ref="webglApp" class="web-gl__canvas" />
    <div class="web-gl__pin-labels" :style="{ display: debugEnabled ? false : 'none' }">
      <p class="web-gl__user-label-canvas">{{ userLabelText }}</p>
      <canvas ref="userLabelCanvas" class="web-gl__user-label-canvas" />

      <!-- this canvas is just for font style extraction -->
      <canvas ref="referenceCanvas" class="web-gl__pin-canvas" />
      <div
        v-for="{ companyValue, id } in currentPins"
        :key="`pinCanvas_${id}`"
        style="display: flex; align-items: center"
      >
        <p v-if="debugEnabled" class="web-gl__pin-canvas" style="margin-right: 10px">
          {{ companyValue }}
        </p>
        <canvas ref="pinCanvas" class="web-gl__pin-canvas" />
      </div>
    </div>
  </div>
</template>

<script>
import store, { setWebGlLoaded, getCompanyName } from '../../store';
import { fetcher } from '../../services/apiService';
import WebGLApp from './webGLApp.js';
import { extractCanvasStyles, drawTextToCanvas } from './canvasTextHelper';

const DEBUG_LABELS = false;
const ENABLE_GET_USER_LOCATION_FROM_IP = true;
const DEFAULT_USER_LOCATION = { lat: 49.291515, long: 8.646999 }; // Waldorf

const PIN_LABEL_CONTEXT_OPTIONS = { shadowColor: 'rgba(0,0,0,1)' };

const getUserLocation = async () => {
  if (!ENABLE_GET_USER_LOCATION_FROM_IP) return;

  try {
    const { latitude: lat, longitude: long } = await fetcher('https://get.geojs.io/v1/ip/geo.json');
    return { lat, long };
  } catch (e) {
    // ignore
  }
};

/**
 * Mobile webbrowser have the behavior that the url bar disappears when scrolling down
 * and appears when scrolling up. The canvas is rendered based on the "largest possible viewport"
 * but centered vertically by the current visible viewport (see style definition .web-gl__canvas).
 * This has the disadvantage that when you scrolling down (the viewport gets bigger because the url
 * bar disappers), you are able to see the end of the canvas and the background behind it
 * at the bottom, which looks wrong. To avoid this, we can render a larger canvas.
 *
 * Also relevant: https://developers.google.com/web/updates/2016/12/url-bar-resizing
 */
const ADDITIONAL_CANVAS_HEIGHT = 60;

export default {
  name: 'WebGL',

  data() {
    return {
      webGLApp: null,
      userLocationPromise: getUserLocation(),

      pinLabelFontsLoaded: false,
      pinLabelColor: undefined,
      pinLabelFontShorthand: '',
      pinLabelsGroupIndex: undefined,
    };
  },

  computed: {
    companyName: getCompanyName,

    debugEnabled() {
      return process.env.NODE_ENV !== 'production' && DEBUG_LABELS;
    },

    currentPins() {
      if (
        (!store.companyPinsGrouped && !store.companyPinsGrouped.length) ||
        this.pinLabelsGroupIndex === undefined
      ) {
        return [];
      }
      return store.companyPinsGrouped[this.pinLabelsGroupIndex].pins;
    },

    userLabelText() {
      if (!store.userModel) return;

      const userLabelText = this.$t('kpi.yourCompanyValue.your', {
        companyName: this.companyName,
        companyValue: this.$t('kpi.yourCompanyValue.companyValue'),
      });

      return `${(store.userModel.companyValue * 100).toFixed(1)} – ${userLabelText}`;
    },
  },

  mounted() {
    this.preparePinLabels();
    this.initWebGLApp();
  },

  methods: {
    async preparePinLabels() {
      // extract styles from one label from css
      const { color, fontShorthand } = extractCanvasStyles(this.$refs.referenceCanvas);
      this.pinLabelColor = color;
      this.pinLabelFontShorthand = fontShorthand;

      // force loading of required font set
      // we used here document.fonts.onloadingdone before, but that wasn't
      // called in safari
      await document.fonts.load(fontShorthand);
      this.pinLabelFontsLoaded = true;
    },

    async initWebGLApp() {
      this.webGLApp = new WebGLApp();
      await this.webGLApp.init(
        this.$refs.webglApp,
        this.$el.clientWidth,
        this.$el.clientHeight + ADDITIONAL_CANVAS_HEIGHT,
        `${process.env.BASE_URL}`
      );

      setWebGlLoaded(true);

      // register listeners

      this.webGLApp.onPinClicked = pin => store.selectCompanyPin(pin);
      this.webGLApp.onUserPinClicked = () => store.setIsUserPinSelected(true);
      this.webGLApp.onUserRotation = location => store.setUserLocation(location);

      this.webGLApp.onLabelsRequested = index => {
        this.pinLabelsGroupIndex = index;
      };

      const userLocation = await this.userLocationPromise;

      // set inititial data
      this.setUserLocation(userLocation || DEFAULT_USER_LOCATION);

      // initialize watchers
      this.setUserLabel();
      this.setCurrentPinLabels();
      this.setCurrentGlobeState();
      this.setCurrentCompanyPins();
      this.setCurrentCompanyColor();

      // TODO: throttle
      window.addEventListener('resize', this.resizeCanvas, false);
      this.$once('hook:beforeDestroy', () =>
        window.removeEventListener('resize', this.resizeCanvas)
      );
    },

    resizeCanvas() {
      if (!this.webGLApp) return;
      this.webGLApp.onResize(
        this.$el.clientWidth,
        this.$el.clientHeight + ADDITIONAL_CANVAS_HEIGHT
      );
    },

    setUserLocation({ lat, long }) {
      this.webGLApp.userLat = lat;
      this.webGLApp.userLong = long;
    },

    setUserLabel() {
      const { userLabelCanvas } = this.$refs;
      const { color, fontShorthand } = extractCanvasStyles(userLabelCanvas);

      this.$watch(
        'userLabelText',
        userLabelText => {
          drawTextToCanvas(userLabelText, userLabelCanvas, fontShorthand, color);
        },
        { immediate: true }
      );

      this.$watch(
        () => this.$route.name === 'companyDetail',
        showUserLabel => {
          if (showUserLabel) {
            this.webGLApp.setUserLabel(userLabelCanvas);
          } else {
            this.webGLApp.removeUserLabel();
          }
        },
        { immediate: true }
      );
    },

    setCurrentPinLabels() {
      // one callback for multiple watchers
      // https://github.com/vuejs/vue/issues/844#issuecomment-335026928
      this.$watch(
        () => ({
          pinLabelFontsLoaded: this.pinLabelFontsLoaded,
          currentPins: this.currentPins,
        }),
        ({ pinLabelFontsLoaded, currentPins }) => {
          if (pinLabelFontsLoaded && currentPins.length) {
            // draw text to each canvas with extracted styles
            currentPins.forEach(({ companyValue }, index) => {
              const pinCanvas = this.$refs.pinCanvas[index];
              const text = (companyValue * 100).toFixed(1);
              drawTextToCanvas(
                text,
                pinCanvas,
                this.pinLabelFontShorthand,
                this.pinLabelColor,
                PIN_LABEL_CONTEXT_OPTIONS
              );
            });

            this.webGLApp.setLabels(this.$refs.pinCanvas, currentPins);
          }
        },
        { deep: true, immediate: true }
      );
    },

    setCurrentCompanyPins() {
      this.$watch(
        () => store.companyPinsGrouped,
        companyPinsGrouped => {
          if (companyPinsGrouped && companyPinsGrouped.length) {
            this.webGLApp.setPins(companyPinsGrouped);
          }
        },
        { immediate: true }
      );
    },

    setCurrentGlobeState() {
      this.$watch(
        () => store.globeState,
        globeState => {
          if (typeof globeState !== 'object') {
            this.webGLApp.setStateById(globeState);
          } else {
            this.webGLApp.transitionBetweenById(
              globeState.current,
              globeState.target,
              globeState.transitionValue
            );
          }
        },
        { deep: true, immediate: true }
      );
    },

    setCurrentCompanyColor() {
      this.$watch(
        () => store.userModel,
        userModel => {
          if (userModel && userModel.companyColor) {
            this.webGLApp.reticle.pinColor = userModel.companyColor;
          }
        },
        { deep: true, immediate: true }
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.web-gl {
  overflow: hidden;
}

.web-gl__canvas {
  position: fixed;
  top: 0;
  left: 0;
  transform: translateY(-10%);
  @include helper__greater($bp-768) {
    top: 50%;
    left: 50%;
    transform: translateX(-50%) translateY(-50%);
  }

  @include helper__until($bp-768) {
    transition: all 200ms ease;
  }
}

.web-gl__button {
  position: absolute;
  top: 20px;
  width: 200px;
  right: 20px;
  pointer-events: normal;
}

.web-gl__pin-labels {
  position: absolute;
  right: 10%;
  top: 100px;
}

.web-gl__user-label-canvas {
  font-size: 40px; // good base texture size
}

// Since the canvas context does not support numeric numbers easily
// we have to utilize two little hacks. Start by reading comment (1)

@font-face {
  @include sapRegular;
  font-family: 'BentonSans-Regular-Numeric';

  // 3) Enable numeric font-feature settings for this font-family name by default
  // numeric numbers
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
}

@mixin numericFontHack() {
  // 1) By enabling numeric numbers on the canvas
  // the font rendering somehow inherits this property...

  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';

  // 2) this doesn't work in firefox though
  // to make it work we redefine the desired font again under a different name:
  font-family: 'BentonSans-Regular-Numeric', SAP, $fallback-fonts;

  // for this font-family we enable the numeric font-feature settings by default (see 3) above
}

.web-gl__pin-canvas {
  @include numericFontHack;

  display: block;
  font-size: 40px; // good base texture size
}
</style>
